import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

// Services
import tripPreferenceApi from "Services/TripPreference";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PreferenceUpdateModal = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [iconPreview, setIconPreview] = useState("/assets/images/logos/no-image.png");
  const [initialData, setInitialData] = useState({
    name: "",
    icon: "",
    status: 1,
  });

  const maxFileSize = 5242880;

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than 5 MB`);
      return {
        code: "file-too-large",
        message: `File size is larger than 5 MB`,
      };
    }
    return null;
  };

  const { getRootProps: rootForIcon, getInputProps: inputForIcon } = useDropzone({
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("icon", file);
          setIconPreview(base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const getPreferenceDetails = useCallback(() => {
    if (!id) return;
    dispatch(showLoader("Loading details..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        const item = res?.data?.data;
        setInitialData({
          name: item?.name || "",
          icon: item?.icon || "",
          status: item?.status !== undefined ? item?.status : 1,
        });
        if (item?.icon) {
          setIconPreview(item.icon);
        }
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error fetching details",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    tripPreferenceApi.getTripPreferenceById(id).then(onSuccess, onFailure);
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog && id) {
      getPreferenceDetails();
    }
  }, [openDialog, id, getPreferenceDetails]);

  const handleUpdatePreference = (formValues) => {
    dispatch(showLoader("Updating..."));

    const formData = new FormData();
    formData.append("name", formValues.name.trim());
    formData.append("status", formValues.status);
    if (formValues.icon && typeof formValues.icon !== "string") {
      formData.append("icon", formValues.icon);
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Trip Preference Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        handleModalClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error updating preference",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    tripPreferenceApi.updateTripPreference(id, formData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: initialData,
    validationSchema: yup.object({
      name: yup.string().required("Preference Name is required").nullable(),
      status: yup.number().required("Status is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdatePreference,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setIconPreview("/assets/images/logos/no-image.png");
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      maxWidth="sm"
      className={classes.root}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>Edit Trip Preference</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeButton}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Icon Upload */}
          <Grid item xs={12}>
            <Typography className={classes.uploadLabel}>PREFERENCE ICON</Typography>
            <Box {...rootForIcon()} className={classes.uploadBox}>
              <input {...inputForIcon()} />
              {iconPreview && iconPreview !== "/assets/images/logos/no-image.png" ? (
                <img src={iconPreview} alt="Icon Preview" className={classes.previewImg} />
              ) : (
                <FileUploadOutlinedIcon className={classes.uploadArrowIcon} />
              )}
              <Box className={classes.updateIconPill}>
                <CameraAltOutlinedIcon className={classes.cameraIcon} />
                <Typography className={classes.updateIconText}>Change Icon</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Preference Name */}
          <Grid item xs={12}>
            <Typography className={classes.inputFieldLabel}>Preference Name</Typography>
            <TextField
              name="name"
              id="name"
              placeholder="e.g. Quiet Ride, Pets, Baby Booster"
              value={formik?.values?.name}
              onChange={formik.handleChange}
              error={formik?.errors?.name && formik?.touched?.name}
              helperText={
                formik?.errors?.name &&
                formik?.touched?.name &&
                formik?.errors?.name
              }
              fullWidth
              variant="outlined"
              size="small"
              className={classes.customTextField}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <Typography className={classes.inputFieldLabel}>Status</Typography>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                name="status"
                id="status"
                value={formik?.values?.status}
                onChange={formik.handleChange}
                className={classes.customSelect}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box className={classes.actionButtonsContainer}>
              <Button
                variant="outlined"
                className={classes.closeButtonAction}
                onClick={handleModalClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.submitButtonAction}
                onClick={formik?.handleSubmit}
              >
                Update Preference
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PreferenceUpdateModal;
